import { useRef, useState } from "react";
import Input from "../CommonComponents/Input/Input";
import { ErrorToast, SuccessToast } from "../toastMessage";
import { availableDirections } from "../../pages/LevelTwo/SelectDirection";
/**
 * The ready-made plot shape list is no longer offered on this step; the
 * reviewer browses for their own image. Kept for when the list comes back.
 * import SelectPlotShape from "../../pages/LevelTwo/SelectPlotShape";
 */
import {
  TSchematicImageDimensions,
  useUploadSchematicImage,
} from "../../hooks/schematic-plan.hooks";
import {
  readSchematicImageValues,
  saveSchematicImageValues,
} from "../../utils/schematicImageStore";
import { SCHEMATIC_IMAGE_ACCEPT, schematicImageProblem } from "./schematicFileRules";

/**
 * The plot variables, all optional lengths in metres: the four sides, AA'
 * and BB', and DE and EF for a plot with more sides. An empty one is saved
 * as 0 and printed blank on the certificate.
 * BEFORE: type TSideKey = "ab" | "bc" | "cd" | "da"; (all required)
 */
type TSideKey = "ab" | "bc" | "cd" | "da" | "aa" | "bb" | "de" | "ef";

/**
 * Labels are the side names only; the section hint says they are in metres.
 * BEFORE: "Schematic Dimension: AB [mtr]" ... "Schematic Dimension: BB' [mtr]"
 */
const SIDES: { key: TSideKey; label: string }[] = [
  { key: "ab", label: "AB" },
  { key: "bc", label: "BC" },
  { key: "cd", label: "CD" },
  { key: "da", label: "DA" },
  { key: "de", label: "DE" },
  { key: "ef", label: "EF" },
  { key: "aa", label: "AA'" },
  { key: "bb", label: "BB'" },
];

type Props = {
  applicationId: number;
  /**
   * The review level, which decided whose plot shape list was read. Unused
   * while the list is not offered; kept so the callers stay unchanged.
   */
  level: 1 | 2 | 3;
  /** The application number, which keys the values kept for the certificate step. */
  applicationNumber: string | undefined;
  /** What was last saved with the certificate data, used when this tab kept nothing. */
  saved: TSchematicImageDimensions | null | undefined;
  /** False when the choice was made before this level (the values can still be edited). */
  canChoose: boolean;
  isSaving: boolean;
  onBack: () => void;
  /** Called once the values are complete and kept for the certificate step. */
  onNext: () => void;
};

/** The arrow each direction value prints, as the backend maps 1-4. */
const DIRECTION_LABELS: Record<string, string> = { "1": "Down", "2": "Left", "3": "Up", "4": "Right" };

/** Where the selected map came from, shown under its preview. */
type TImageSource = "saved" | "shape" | "upload";
const IMAGE_SOURCE_LABELS: Record<TImageSource, string> = {
  saved: "Saved earlier",
  shape: "From the plot shapes list",
  upload: "Your uploaded image",
};

/** A numbered section title with a short line on what the section is for. */
function SectionHeading({
  id,
  number,
  title,
  hint,
  required = false,
}: {
  id?: string;
  number: number;
  title: string;
  hint: string;
  required?: boolean;
}) {
  return (
    <div className="mb-3">
      <h3 id={id} className="flex items-center gap-2 text-base font-bold text-[#423F3F]">
        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-primary text-white text-xs">
          {number}
        </span>
        {title}
        {required && <span className="text-red-500">*</span>}
      </h3>
      <p className="text-xs text-gray-500 mt-1 ml-8">{hint}</p>
    </div>
  );
}

/** A side as the input shows it: empty for 0 or nothing. */
const sideText = (value: unknown) => (Number(value) > 0 ? String(value) : "");

/**
 * The body of the Schematic Plan step for "Upload image + dimensions": one
 * JPG/PNG of the plot and one direction (both required), and the optional
 * plot variables (AB ... EF). The certificate shows them inside the
 * SCHEMATIC PLAN box, as in Gondia.
 *
 * The image is a JPG/PNG the reviewer browses for (the ready-made plot
 * shapes list is not offered any more). An uploaded image is sent as soon as it is picked; the sides and direction
 * stay on this screen. Next keeps all of them for the certificate step
 * (utils/schematicImageStore.ts), which saves them with the certificate
 * data; the choice itself is saved by SchematicPlanStep.
 */
export function SchematicImageDimensionsPanel({
  applicationId,
  /** BEFORE: level, (read only by the plot shape list, now commented out) */
  applicationNumber,
  saved,
  canChoose,
  isSaving,
  onBack,
  onNext,
}: Props) {
  /** Values this tab already kept win over the saved ones: they are newer. */
  const kept = readSchematicImageValues(applicationNumber);
  const inputRef = useRef<HTMLInputElement>(null);
  const upload = useUploadSchematicImage(applicationId);

  const [imageUrl, setImageUrl] = useState<string>(kept.plotShape || saved?.imageUrl || "");
  const [sides, setSides] = useState<Record<TSideKey, string>>({
    ab: sideText(kept.ab ?? saved?.ab),
    bc: sideText(kept.bc ?? saved?.bc),
    cd: sideText(kept.cd ?? saved?.cd),
    da: sideText(kept.da ?? saved?.da),
    aa: sideText(kept.aa ?? saved?.aa),
    bb: sideText(kept.bb ?? saved?.bb),
    de: sideText(kept.de ?? saved?.de),
    ef: sideText(kept.ef ?? saved?.ef),
  });
  /**
   * BEFORE - "AA' and BB'" as one optional free-text box:
   * const [aaBb, setAaBb] = useState<string>(kept.aaBb ?? saved?.aaBb ?? "");
   */
  const [direction, setDirection] = useState<string>(kept.direction || saved?.direction || "");
  const [showErrors, setShowErrors] = useState(false);
  /** BEFORE: const [isShapeListOpen, setIsShapeListOpen] = useState(false); */
  const [imageSource, setImageSource] = useState<TImageSource>("saved");

  /**
   * BEFORE - every side was required:
   * const sideMissing = (key: TSideKey) => !(Number(sides[key]) > 0);
   */

  const send = (picked: File | undefined) => {
    if (!picked || upload.isPending) return;
    const problem = schematicImageProblem(picked);
    if (problem) {
      ErrorToast(problem);
      return;
    }
    upload.mutate(picked, {
      onSuccess: (result) => {
        if (result?.success && result.data?.url) {
          setImageUrl(result.data.url);
          setImageSource("upload");
          SuccessToast("Plot image uploaded");
        }
      },
    });
  };

  const next = () => {
    setShowErrors(true);
    if (!imageUrl) {
      ErrorToast("Upload the plot image first");
      return;
    }
    /**
     * The plot variables are optional, so Next no longer checks them.
     * BEFORE:
     * if (SIDES.some(({ key }) => sideMissing(key))) {
     *   ErrorToast("Enter all dimensions (AB, BC, CD, DA, AA', BB')");
     *   return;
     * }
     */
    if (!direction) {
      ErrorToast("Select the direction");
      return;
    }
    saveSchematicImageValues(applicationNumber, {
      plotShape: imageUrl,
      ab: Number(sides.ab),
      bc: Number(sides.bc),
      cd: Number(sides.cd),
      da: Number(sides.da),
      /** BEFORE: aaBb: aaBb.trim(), */
      aa: Number(sides.aa),
      bb: Number(sides.bb),
      de: Number(sides.de),
      ef: Number(sides.ef),
      direction,
    });
    onNext();
  };

  /**
   * Centered at the same width as the "Upload file" view in SchematicPlanStep.
   * BEFORE: <div className="p-6">
   */
  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h2 className="text-[#423F3F] text-xl font-bold mb-1 text-center">Schematic Plan</h2>
      {!canChoose && (
        <p className="text-sm text-gray-600 mb-4">
          An image with dimensions was forwarded to you. You can change it before Save And Preview.
        </p>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={SCHEMATIC_IMAGE_ACCEPT}
        className="hidden"
        onChange={(event) => {
          send(event.target.files?.[0]);
          event.target.value = "";
        }}
      />

      {/*
        Three labelled sections so each picker says what it is for: the four
        sides, the plot map (ready-made shape OR the reviewer's own image) and
        the direction arrow.
        BEFORE - one grid with the four sides, a bare "Select Map" upload icon
        with "OR Browse" beside it, and an unlabelled row of arrows under
        "Select Direction".
      */}
      <div className="space-y-4 max-w-4xl">
        <section className="border border-gray-200 rounded-xl p-4 bg-white">
          {/* BEFORE: title="Plot sides" */}
          <SectionHeading number={1} title="Plot Variable" hint="Length of each side in metres, as written on the certificate." />
          <div className="grid grid-cols-2 gap-4">
            {SIDES.map(({ key, label }) => (
              <div key={key} className="col-span-2 md:col-span-1">
                {/*
                  BEFORE - required, with "* Required" under an empty one:
                  required
                  error={showErrors && sideMissing(key)}
                  helperText={showErrors && sideMissing(key) ? "* Required" : undefined}
                */}
                <Input
                  type="number"
                  min={0}
                  step="any"
                  label={label}
                  placeholder={label}
                  value={sides[key]}
                  onWheel={(event) => event.currentTarget.blur()}
                  onChange={(event) => setSides((prev) => ({ ...prev, [key]: event.target.value }))}
                />
              </div>
            ))}
            {/*
              BEFORE - one optional free-text box, now AA' and BB' rows in SIDES:
              <div className="col-span-2 md:col-span-1">
                <Input
                  label="AA' and BB'"
                  placeholder="AA' and BB' (optional)"
                  maxLength={64}
                  value={aaBb}
                  onChange={(event) => setAaBb(event.target.value)}
                />
              </div>
            */}
          </div>
        </section>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <section
            className={`border rounded-xl p-4 bg-white ${
              showErrors && !imageUrl ? "border-red-500" : "border-gray-200"
            }`}
          >
            <SectionHeading number={2} title="Select Map" required hint="The plot image shown in the certificate's Schematic Plan box." />
            {/*
              BEFORE - "Choose a plot shape" (ready-made list) OR Browse:
              <div className="grid grid-cols-[1fr_auto_1fr] items-stretch gap-2">
                <button
                  type="button"
                  onClick={() => setIsShapeListOpen(true)}
                  className="border-2 border-gray-200 rounded-lg p-3 flex flex-col items-center justify-center text-center gap-1 hover:border-primary hover:bg-blue-50 transition-colors"
                >
                  <i className="fa fa-th-large text-blue-500 text-xl"></i>
                  <span className="font-semibold text-sm text-[#423F3F]">Choose a plot shape</span>
                  <span className="text-xs text-gray-500">From the ready-made list</span>
                </button>
                <span className="self-center text-xs font-bold text-gray-400">OR</span>
            */}
            <div className="grid grid-cols-1 items-stretch gap-2">
              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                disabled={upload.isPending}
                className="border-2 border-gray-200 rounded-lg p-3 flex flex-col items-center justify-center text-center gap-1 hover:border-primary hover:bg-blue-50 transition-colors disabled:opacity-60"
              >
                <i className="fa fa-upload text-blue-500 text-xl"></i>
                <span className="font-semibold text-sm text-[#423F3F]">
                  {upload.isPending ? "Uploading…" : "Browse map image"}
                </span>
                <span className="text-xs text-gray-500">JPG or PNG, up to 10 MB</span>
              </button>
            </div>

            <div className="mt-3 border border-dashed border-gray-300 rounded-lg p-3 flex items-center gap-3 min-h-[96px]">
              {imageUrl ? (
                <>
                  <img
                    className="w-20 h-20 object-contain rounded border border-gray-200 bg-white shrink-0"
                    src={imageUrl}
                    alt="Selected plot map"
                  />
                  <div className="min-w-0">
                    <p className="font-semibold text-sm text-[#423F3F]">Selected map</p>
                    <p className="text-xs text-gray-500">{IMAGE_SOURCE_LABELS[imageSource]}</p>
                  </div>
                </>
              ) : (
                <p className={`text-sm w-full text-center ${showErrors ? "text-red-500" : "text-gray-500"}`}>
                  {/* BEFORE: "* Required: choose a plot shape or browse an image" */}
                  {showErrors ? "* Required: browse a map image" : "No map selected yet"}
                </p>
              )}
            </div>
          </section>

          <section
            className={`border rounded-xl p-4 bg-white ${
              showErrors && !direction ? "border-red-500" : "border-gray-200"
            }`}
          >
            <SectionHeading
              id="schematic-direction"
              number={3}
              title="Select Direction"
              required
              hint="The arrow printed next to the map."
            />
            <div role="radiogroup" aria-labelledby="schematic-direction" className="grid grid-cols-4 gap-2">
              {availableDirections.map((option) => {
                const selected = direction === option.id;
                return (
                  <button
                    key={option.id}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    onClick={() => setDirection(option.id)}
                    className={`relative p-2 flex flex-col items-center gap-1 rounded-lg border-2 transition-colors ${
                      selected ? "border-primary bg-blue-50" : "border-gray-200 hover:bg-gray-50"
                    }`}
                  >
                    {selected && (
                      <i className="fa fa-check-circle text-primary absolute top-1 right-1 text-sm"></i>
                    )}
                    <img src={option.direction} className="max-w-full h-16 object-contain" alt="" />
                    <span className={`text-xs ${selected ? "font-bold text-primary" : "text-gray-600"}`}>
                      {DIRECTION_LABELS[option.id]}
                    </span>
                  </button>
                );
              })}
            </div>
            {showErrors && !direction && <p className="mt-2 text-sm text-red-500">* Required: select a direction</p>}
          </section>
        </div>
      </div>

      {/*
        BEFORE - the ready-made plot shapes list, opened by "Choose a plot shape":
        {isShapeListOpen && (
          <SelectPlotShape
            isLevel1={level === 1}
            isLevel3={level === 3}
            selected={imageUrl}
            onClose={() => setIsShapeListOpen(false)}
            onSelect={(shapeUrl) => {
              setImageUrl(shapeUrl);
              setImageSource("shape");
              setIsShapeListOpen(false);
            }}
          />
        )}
      */}

      <div className="flex flex-col sm:flex-row justify-center gap-3 mt-6">
        <button
          type="button"
          className="bg-black text-white px-10 py-2 grow md:grow-0 rounded-full font-bold"
          onClick={onBack}
        >
          Back
        </button>
        <button
          type="button"
          className="bg-primary px-10 py-2 grow md:grow-0 rounded-full font-bold text-white disabled:bg-gray-300 disabled:text-gray-500"
          disabled={isSaving || upload.isPending}
          onClick={next}
        >
          Next
        </button>
      </div>
    </div>
  );
}
